import { useState } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import {
  Shield,
  Check,
  X,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronUp,
  Bot,
  Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminApprovals() {
  const { user, pendingApprovals, decideApproval, fetchCommandCenter } = useAdminBoard();
  const [expandedId, setExpandedId] = useState(null);
  const [decisionReason, setDecisionReason] = useState("");
  const [filter, setFilter] = useState("all");
  const [processing, setProcessing] = useState(null);

  // RBAC check
  const canApprove = user?.role === "gsk_super_admin" || user?.role === "client_admin";

  const handleApprove = async (gateId) => {
    setProcessing(gateId);
    try {
      await decideApproval(gateId, true, decisionReason || "Approved by admin");
      toast.success("Action APPROVED");
      setExpandedId(null);
      setDecisionReason("");
      fetchCommandCenter();
    } catch (error) {
      toast.error("Failed to approve");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (gateId) => {
    if (!decisionReason.trim()) {
      toast.error("Rejection requires a reason");
      return;
    }

    setProcessing(gateId);
    try {
      await decideApproval(gateId, false, decisionReason);
      toast.success("Action REJECTED");
      setExpandedId(null);
      setDecisionReason("");
      fetchCommandCenter();
    } catch (error) {
      toast.error("Failed to reject");
    } finally {
      setProcessing(null);
    }
  };

  const filteredApprovals = pendingApprovals.filter(approval => {
    if (filter === "all") return true;
    return approval.risk_level === filter;
  });

  const getRiskColor = (level) => {
    switch (level) {
      case "critical": return "bg-red-500 text-white";
      case "high": return "bg-orange-500 text-white";
      case "medium": return "bg-amber-500 text-white";
      default: return "bg-slate-400 text-white";
    }
  };

  const getRiskBorder = (level) => {
    switch (level) {
      case "critical": return "border-red-300 bg-red-50";
      case "high": return "border-orange-300 bg-orange-50";
      case "medium": return "border-amber-300 bg-amber-50";
      default: return "border-slate-200 bg-slate-50";
    }
  };

  if (!canApprove) {
    return (
      <div className="p-6 bg-slate-50 min-h-full flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-slate-400" />
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Access Restricted</h2>
            <p className="text-slate-500 text-sm">
              You don't have permission to approve/reject AI actions.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6 bg-slate-50 min-h-full" data-testid="admin-approvals">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Pending Approvals</h1>
          <p className="text-slate-500 text-sm mt-1">
            Review and decide on AI actions - Your decision is final
          </p>
        </div>

        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 bg-white border-slate-300">
            <Filter className="w-4 h-4 mr-2 text-slate-500" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-amber-600">{pendingApprovals.length}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-600">
                  {pendingApprovals.filter(a => a.risk_level === "critical").length}
                </p>
                <p className="text-xs text-slate-500">Critical</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-orange-600">
                  {pendingApprovals.filter(a => a.risk_level === "high").length}
                </p>
                <p className="text-xs text-slate-500">High Risk</p>
              </div>
              <Shield className="w-8 h-8 text-orange-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-600">
                  {pendingApprovals.filter(a => a.risk_level === "medium" || a.risk_level === "low").length}
                </p>
                <p className="text-xs text-slate-500">Medium/Low</p>
              </div>
              <Bot className="w-8 h-8 text-slate-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Approvals List */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="w-4 h-4 text-amber-600" />
            Actions Requiring Review
          </CardTitle>
          <CardDescription className="text-xs">
            AI recommends. You decide. All decisions are logged permanently.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredApprovals.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Check className="w-14 h-14 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No pending approvals</p>
              <p className="text-sm">All AI actions have been reviewed</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredApprovals.map((approval) => (
                <div
                  key={approval.id}
                  className={`border rounded-lg overflow-hidden ${getRiskBorder(approval.risk_level)}`}
                  data-testid={`approval-item-${approval.id}`}
                >
                  {/* Header - Clickable to expand */}
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/50 transition-colors"
                    onClick={() => setExpandedId(expandedId === approval.id ? null : approval.id)}
                  >
                    <div className="flex items-center gap-4">
                      <Badge className={getRiskColor(approval.risk_level)}>
                        {approval.risk_level?.toUpperCase()}
                      </Badge>
                      <div>
                        <p className="font-medium text-slate-900">{approval.action_description}</p>
                        <p className="text-sm text-slate-500">
                          {approval.action_type} • Session: {approval.session_id?.slice(0, 8) || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {approval.ai_confidence && (
                        <div className="text-right mr-4">
                          <p className="text-xs text-slate-500">AI Confidence</p>
                          <p className="text-sm font-semibold text-slate-700">
                            {Math.round(approval.ai_confidence * 100)}%
                          </p>
                        </div>
                      )}
                      {expandedId === approval.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === approval.id && (
                    <div className="p-4 border-t border-slate-200 bg-white">
                      {/* AI Recommendation Box */}
                      <div className="mb-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="w-4 h-4 text-cyan-600" />
                          <span className="text-sm font-medium text-cyan-700">
                            AI Recommendation — Human Approval Required
                          </span>
                        </div>
                        <p className="text-sm text-slate-700">
                          {approval.risk_reason || "The AI believes this action is appropriate based on the conversation context."}
                        </p>
                      </div>

                      {/* Action Details */}
                      {approval.action_data && Object.keys(approval.action_data).length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-slate-600 mb-2">Action Details:</p>
                          <pre className="text-xs bg-slate-100 p-3 rounded overflow-x-auto text-slate-700 border border-slate-200">
                            {JSON.stringify(approval.action_data, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* Decision Reason Input */}
                      <div className="mb-4">
                        <label className="text-sm font-medium text-slate-600 mb-2 block">
                          Decision Reason (required for rejection)
                        </label>
                        <Textarea
                          placeholder="Explain your decision..."
                          className="bg-white border-slate-300 text-slate-900"
                          value={expandedId === approval.id ? decisionReason : ""}
                          onChange={(e) => setDecisionReason(e.target.value)}
                          data-testid={`reason-${approval.id}`}
                        />
                      </div>

                      {/* Action Buttons - Large and Prominent */}
                      <div className="flex gap-3">
                        <Button
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-5"
                          onClick={() => handleApprove(approval.id)}
                          disabled={processing === approval.id}
                          data-testid={`approve-${approval.id}`}
                        >
                          <Check className="w-5 h-5 mr-2" />
                          {processing === approval.id ? "Processing..." : "APPROVE"}
                        </Button>
                        <Button
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-5"
                          onClick={() => handleReject(approval.id)}
                          disabled={processing === approval.id}
                          data-testid={`reject-${approval.id}`}
                        >
                          <X className="w-5 h-5 mr-2" />
                          {processing === approval.id ? "Processing..." : "REJECT"}
                        </Button>
                      </div>

                      {/* Timestamp */}
                      <p className="text-xs text-slate-500 mt-4 text-center">
                        Requested: {new Date(approval.created_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
